import { baseApi } from '../../services/baseApi';

/** Counts returned by the data-pull status endpoint. */
export interface SyncCounts {
  states?: number;
  districts?: number;
  parliaments?: number;
  assemblies?: number;
  booths?: number;
  sections?: number;
  voter_files?: number;
  voters?: number;
}

export interface SyncStatus {
  level: string;
  code: string | null;
  synced: boolean;
  counts: SyncCounts;
}

export interface SyncJob {
  id: number;
  kind: string;
  code: string | null;
  code_name?: string | null;
  status: 'queued' | 'running' | 'done' | 'failed';
  message?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const dataSyncApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ECI data-pull status (system-admin only). Pass level+code to scope it.
    getSyncStatus: build.query<SyncStatus, { level?: string; code?: string } | void>({
      query: (arg) => ({
        url: 'access/data-sync/status/',
        params: arg && arg.level && arg.code ? { level: arg.level, code: arg.code } : {},
      }),
      providesTags: ['SyncJobs'],
    }),
    // launch a hierarchy sync in the background
    runSync: build.mutation<SyncJob, { kind: string; code?: string }>({
      query: (body) => ({ url: 'access/data-sync/run/', method: 'POST', body }),
      invalidatesTags: ['SyncJobs'],
    }),
    // recent jobs (poll this for progress)
    getSyncJobs: build.query<SyncJob[], void>({
      query: () => ({ url: 'access/data-sync/jobs/' }),
      providesTags: ['SyncJobs'],
    }),
    // Step 1 (web captcha): fetch a fresh captcha image to show the user
    getCaptcha: build.mutation<{ captchaId: string; image: string }, void>({
      query: () => ({ url: 'access/data-sync/captcha/' }),
    }),
    // Step 1 (web captcha): submit the typed answer -> saves e-roll paths
    submitErollPaths: build.mutation<
      { ac_code: string; stations: number; message: string },
      { ac_code: string; version: string; language: string; captchaId: string; captcha: string }
    >({
      query: (body) => ({ url: 'access/data-sync/eroll-paths/', method: 'POST', body }),
    }),
    // Parliament name editor: list a state's PCs (with transliteration guess)
    getStatePcs: build.query<PcRow[], { state: string }>({
      query: ({ state }) => ({ url: 'access/data-sync/pcs/', params: { state } }),
      providesTags: ['SyncJobs'],
    }),
    // save the corrected English name + category for one parliament
    savePcName: build.mutation<
      PcRow,
      { pc_code: string; name: string; category?: string }
    >({
      query: (body) => ({ url: 'access/data-sync/pc-name/', method: 'POST', body }),
      invalidatesTags: ['SyncJobs'],
    }),
    // save all edited parliament names in ONE request
    savePcNamesBulk: build.mutation<
      { saved: PcRow[]; saved_count: number; skipped: number },
      { items: { pc_code: string; name: string; category?: string }[] }
    >({
      query: (body) => ({ url: 'access/data-sync/pc-names/', method: 'POST', body }),
      invalidatesTags: ['SyncJobs'],
    }),
    // download the parliament-names Excel template for a state (local + english cols)
    getPcNamesTemplate: build.query<Blob, { state: string }>({
      query: ({ state }) => ({
        url: 'access/data-sync/pc-names/template/',
        params: { state },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
    // upload the filled parliament-names Excel -> saves english names
    importPcNames: build.mutation<
      { rows: number; saved_count: number; skipped: number },
      { file: File }
    >({
      query: ({ file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: 'access/data-sync/pc-names/import/', method: 'POST', body: form };
      },
      invalidatesTags: ['SyncJobs'],
    }),
    // SEC (ward-roll) dropdowns — District -> Body
    getSecDistricts: build.query<SecOption[], void>({
      query: () => ({ url: 'access/data-sync/sec-districts/' }),
    }),
    getSecBodies: build.query<SecOption[], { district: string }>({
      query: ({ district }) => ({ url: 'access/data-sync/sec-bodies/', params: { district } }),
    }),
    // ECINET e-roll captcha relay (Step 1): open a browser session -> captcha
    erollStart: build.mutation<
      {
        session_id: string;
        captcha_image: string;
        options?: Record<string, string[]>;
        missing?: string[];
      },
      {
        session_id?: string; state: string; year?: string; roll?: string;
        district?: string; ac?: string; language?: string; want_captcha?: boolean;
      }
    >({
      query: (body) => ({ url: 'access/data-sync/eroll-start/', method: 'POST', body }),
    }),
    // submit the typed captcha -> download starts in the BACKGROUND (no timeout)
    erollSubmit: build.mutation<{ status: string }, { session_id: string; captcha: string }>({
      query: (body) => ({ url: 'access/data-sync/eroll-submit/', method: 'POST', body }),
    }),
    // poll the background download
    erollStatus: build.mutation<
      { status: string; result?: { downloaded?: string[]; count?: number; note?: string; error?: string } },
      { session_id: string }
    >({
      query: ({ session_id }) => ({
        url: 'access/data-sync/eroll-status/', method: 'GET', params: { session_id },
      }),
    }),
  }),
});

export interface SecOption {
  code: string;
  name: string;
}

export interface PcRow {
  pc_code: string;
  pc_no: number | null;
  name: string;
  category: string | null;
  pc_name_category: string | null;
  is_local?: boolean;
  suggested?: string | null;
}

export const {
  useGetSyncStatusQuery,
  useRunSyncMutation,
  useGetSyncJobsQuery,
  useGetCaptchaMutation,
  useSubmitErollPathsMutation,
  useGetStatePcsQuery,
  useSavePcNameMutation,
  useSavePcNamesBulkMutation,
  useLazyGetPcNamesTemplateQuery,
  useImportPcNamesMutation,
  useGetSecDistrictsQuery,
  useGetSecBodiesQuery,
  useErollStartMutation,
  useErollSubmitMutation,
  useErollStatusMutation,
} = dataSyncApi;
